/*
 * Sizing quiz — Phase 2
 *
 * Single-file vanilla JS. Reads a JSON question registry, drives a
 * multi-path quiz with state preservation, autosave-with-retry, and a
 * blocking final recommendation call. All three backend endpoints are
 * mocked client-side; Phase 3 swaps `mockApi` for `realApi` with the
 * same surface.
 *
 *   1. Boot           — fetch registry, hydrate state, render
 *   2. State          — single object, persisted to localStorage on every
 *                       mutation
 *   3. Renderer       — per-question-type DOM generation + answer restore
 *   4. Navigation     — next / back, with branch + path mutation rules
 *   5. Image swap     — derived from `cup` answer when set
 *   6. Network        — mockApi, start/autosave/complete with retry+queue
 *   7. Loading state  — rotating copy, ≥1.5s minimum
 *   8. Email capture  — gate after the last question, before the report:
 *                       collects email, then reveals a discount code on the
 *                       report. "Skip" proceeds with no email / no code.
 *   9. Results        — recommendation render + discount banner
 */

(function () {
  'use strict';

  // ------------------------------------------------------------------- consts

  // v2: switched from slug answer values to UUIDs. Bumping the storage key
  // discards any pre-UUID localStorage so old answers don't get replayed
  // against the new registry-keyed routing.
  const STORAGE_KEY = 'sizing-quiz:v2:state';
  const ROTATE_MS = 600;
  const MIN_LOADING_MS = 1500;
  const AUTOSAVE_FAILURE_WARN_MS = 5 * 60 * 1000;

  // Email shape check — mirrors the inline check in isOneQuestionValid.
  // Used by the pre-report email-capture gate.
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const DEFAULT_LOADING_MESSAGES = [
    "We're using our proprietary AI algorithm to determine your perfect fit",
    'Analysing your responses…',
    'Calibrating your fit recommendation…',
    'Almost done…'
  ];

  // Inline SVG icons used by the icon+text buttons.
  const ARROW_RIGHT_SVG = '<svg viewBox="0 0 24 24" width="14" height="14"><path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const ARROW_LEFT_SVG  = '<svg viewBox="0 0 24 24" width="14" height="14"><path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const REFRESH_SVG = '<svg viewBox="0 0 24 24" width="14" height="14"><path d="M4 12a8 8 0 0 1 14-5.3M20 12a8 8 0 0 1-14 5.3M18 4v4h-4M6 20v-4h4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // Helper: render a button/anchor styled as a brand nav button (icon+text, no border/fill).
  // tag: 'button' | 'a'
  // variant: 'prev' | 'next'    — `next` = filled circle (primary), `prev` = outline circle (secondary)
  // text: button label
  // attrs: extra HTML attributes string (e.g. 'href="/checkout"' or 'data-foo="bar"')
  // Buttons:
  //   - "prev" / back: stays as a minimal scoped navbtn (back arrow + lowercase text)
  //   - "next" / forward (Next / Submit / Start over / primary CTAs): emits the
  //     theme's own `.button` class so it inherits the black-pill primary
  //     styling the rest of the site uses. No quiz-scoped classes on the
  //     outside, so our CSS doesn't fight `.button`. Trailing arrow is plain
  //     text rather than an SVG icon — matches the prototype's "Begin →" pattern.
  function brandBtn(tag, variant, text, attrs, iconSvg) {
    if (variant === 'prev') {
      iconSvg = iconSvg || ARROW_LEFT_SVG;
      const cls = 'sizing-quiz__navbtn sizing-quiz__navbtn--prev';
      const iconHtml = '<span class="sizing-quiz__navbtn-icon" aria-hidden="true">' + iconSvg + '</span>';
      const textHtml = '<span class="sizing-quiz__navbtn-text">' + escapeHtml(text) + '</span>';
      return '<' + tag + ' class="' + cls + '" ' + (attrs || '') + '>' + iconHtml + textHtml + '</' + tag + '>';
    }
    // next / submit / primary CTA — use the theme's `.button` so the black-pill
    // primary styling cascades through and matches the rest of the storefront.
    return '<' + tag + ' class="button" ' + (attrs || '') + '>' + escapeHtml(text) + ' →</' + tag + '>';
  }

  // Body-figure asset is keyed on TOT band + TOT cup. We ship anchors only;
  // anything between snaps to the nearest-lower anchor in each axis.
  // Mirror of the anchor set in fit-check-test/assets/quiz-figure-tot*.svg.
  const FIGURE_BAND_ANCHORS = [0, 3, 7, 11];
  const FIGURE_CUP_ANCHORS  = [0, 7, 11];

  // Body silhouette layer sits behind the bra-image and provides the skin
  // tone. Live-quiz reverse-engineering showed it varies with band only
  // (not cup) — so we ship 4 anchors instead of 12.
  const SKIN_TONE_BAND_ANCHORS = [0, 3, 7, 11];

  // skin_tone answer slug → rgb. Captured from the live quiz at
  // fit.theonetwo.com; mirrors the swatch values authored in
  // quiz-questions.json so the picker and the body-figure stay in sync.
  const SKIN_TONE_COLORS = {
    'porcelain': 'rgb(228, 203, 184)',
    'fair':      'rgb(222, 183, 159)',
    'light':     'rgb(212, 170, 124)',
    'tan':       'rgb(161, 108, 77)',
    'deep':      'rgb(124, 71, 41)',
    'ebony':     'rgb(82, 66, 57)'
  };
  // Used when no skin_tone answer is set, or the slug isn't in the map.
  const DEFAULT_SKIN_COLOR = 'rgb(161, 108, 77)';

  // -------------------------------------------------------------- one-quiz boot

  function init(root) {
    if (!root || root.dataset.sizingQuizInitialized === '1') return;
    root.dataset.sizingQuizInitialized = '1';

    const cfg = readConfig(root);
    const els = collectElements(root);
    const queryParams = new URLSearchParams(window.location.search);

    // Sizes endpoint is optional — the quiz still works without it, the
    // body-figure just stays on the default image. Resolves to null on any
    // failure so .then() never rejects on the sizes fetch alone.
    const sizesFetch = (function () {
      if (!cfg.apiUrl) return Promise.resolve(null);
      return fetch(cfg.apiUrl.replace(/\/+$/, '') + '/sizes', { credentials: 'omit' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })();

    Promise.all([
      fetch(cfg.registryUrl, { credentials: 'omit' }).then(function (r) {
        if (!r.ok) throw new Error('registry fetch failed: ' + r.status);
        return r.json();
      }),
      fetch(cfg.answerRegistryUrl, { credentials: 'omit' }).then(function (r) {
        if (!r.ok) throw new Error('answer-registry fetch failed: ' + r.status);
        return r.json();
      }),
      sizesFetch
    ])
      .then(function (results) { boot(root, els, cfg, results[0], results[1], queryParams, results[2]); })
      .catch(function (err) {
        console.error('[quiz] failed to load registry', err);
        els.stage.innerHTML =
          '<p class="sizing-quiz__error-body">Sorry — we couldn\'t load the quiz right now. Please refresh and try again.</p>';
      });
  }

  // Build the {tot{band}-tot{cup}: url} map from the 12 data-figure-* attrs
  // emitted by sections/sizing-quiz.liquid. The keys match the asset
  // filenames (quiz-figure-tot{band}-tot{cup}.svg).
  function collectFigureUrls(root) {
    const out = {};
    FIGURE_BAND_ANCHORS.forEach(function (b) {
      FIGURE_CUP_ANCHORS.forEach(function (c) {
        const key = 'tot' + b + '-tot' + c;
        // dataset converts data-figure-tot0-tot7 → figureTot0Tot7
        const datasetKey = 'figureTot' + b + 'Tot' + c;
        const url = root.dataset[datasetKey];
        if (url) out[key] = url;
      });
    });
    return out;
  }

  // {tot{band}: url} map for the 4 body-silhouette mask SVGs. Read from
  // data-skin-tone-tot{band} attrs on the section root.
  function collectSkinToneUrls(root) {
    const out = {};
    SKIN_TONE_BAND_ANCHORS.forEach(function (b) {
      const url = root.dataset['skinToneTot' + b];
      if (url) out[b] = url;
    });
    return out;
  }

  // Generic collector for {key: url} maps from data-{prefix}-{key} attrs.
  // E.g. collectKeyedAssetUrls(root, 'figure', ['back', 'back-above'])
  // reads data-figure-back, data-figure-back-above → { back: url, ... }.
  function collectKeyedAssetUrls(root, prefix, keys) {
    const out = {};
    keys.forEach(function (key) {
      // data-figure-back-just-under → figureBackJustUnder
      const camel = prefix + key.split('-').map(function (part) {
        return part.charAt(0).toUpperCase() + part.slice(1);
      }).join('');
      const url = root.dataset[camel];
      if (url) out[key] = url;
    });
    return out;
  }

  // ------------------------------------------------------------ turnstile

  // Cloudflare Turnstile, loaded once per page in explicit-render mode (the
  // same as the quick quiz's engine).
  const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  let turnstilePromise = null;
  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (turnstilePromise) return turnstilePromise;
    turnstilePromise = new Promise(function (resolve, reject) {
      const s = document.createElement('script');
      s.src = TURNSTILE_SRC;
      s.async = true;
      s.defer = true;
      s.onload = function () {
        if (window.turnstile) resolve(window.turnstile);
        else reject(new Error('Turnstile loaded without window.turnstile'));
      };
      s.onerror = function () {
        turnstilePromise = null;
        reject(new Error('Turnstile failed to load'));
      };
      document.head.appendChild(s);
    });
    return turnstilePromise;
  }

  // Renders a managed widget into `container` (most shoppers never see a
  // challenge). Returns { token(), unavailable() }, or null with no site key:
  // the form then sends no token. If the script can't load (e.g. a blocker),
  // unavailable() turns true and the form submits anyway — the API decides.
  function mountTurnstile(container, siteKey) {
    if (!siteKey || !container) return null;
    let widgetId = null;
    let current = '';
    let failed = false;
    loadTurnstile().then(function (ts) {
      if (!container.isConnected) return;
      widgetId = ts.render(container, {
        sitekey: siteKey,
        appearance: 'interaction-only',
        callback: function (token) { current = token; },
        'expired-callback': function () { current = ''; },
        'error-callback': function () { current = ''; }
      });
    }).catch(function (err) {
      failed = true;
      console.warn('[quiz] Turnstile unavailable', err);
    });
    return {
      token: function () {
        if (current) return current;
        if (window.turnstile && widgetId != null) return window.turnstile.getResponse(widgetId) || '';
        return '';
      },
      unavailable: function () { return failed; }
    };
  }

  function readConfig(root) {
    return {
      registryUrl: root.dataset.registryUrl,
      answerRegistryUrl: root.dataset.answerRegistryUrl,
      apiUrl: root.dataset.apiUrl || '',
      turnstileSiteKey: root.dataset.turnstileSiteKey || '',
      images: {
        // Legacy: only `default` is still consumed — the actual body figures
        // come from `figures` below, keyed by `tot{band}-tot{cup}`.
        default: root.dataset.imageDefault
      },
      figures: collectFigureUrls(root),
      skinTones: collectSkinToneUrls(root),
      // Named view variants — keyed `back`, `back-just-under`, etc.
      // Mirrors the data-figure-{key} / data-skin-tone-{key} attrs on
      // the section root.
      figureVariants: collectKeyedAssetUrls(root, 'figure', [
        'back', 'back-just-under', 'back-above',
        'cups-gaping-a-lot', 'cups-slight-gaping', 'cups-fits-perfectly'
      ]),
      skinToneVariants: collectKeyedAssetUrls(root, 'skinTone', [
        'back', 'back-just-under', 'back-above',
        'cups-gaping-a-lot', 'cups-slight-gaping', 'cups-fits-perfectly',
        'cups-slight-overflow', 'cups-overflowing-a-lot'
      ]),
      flagUrls: {
        'AU/NZ':  root.dataset.flagAuNz,
        'USA':    root.dataset.flagUsa,
        'UK':     root.dataset.flagUk,
        'EU':     root.dataset.flagEu,
        'Italy':  root.dataset.flagItaly,
        'France': root.dataset.flagFrance,
        'Japan':  root.dataset.flagJapan
      }
    };
  }

  function collectElements(root) {
    return {
      root: root,
      intro: root.querySelector('[data-sq-intro]'),
      pageHeader: root.querySelector('[data-sq-page-header]'),
      sectionTitle: root.querySelector('[data-sq-section-title]'),
      image: root.querySelector('[data-sq-image]'),
      imageWrap: root.querySelector('[data-sq-image-wrap]'),
      skinTone: root.querySelector('[data-sq-skin-tone]'),
      hotspot: root.querySelector('[data-sq-hotspot]'),
      progress: root.querySelector('[data-sq-progress]'),
      restartGlobal: root.querySelector('[data-sq-restart-global]'),
      stage: root.querySelector('[data-sq-stage]'),
      nav: root.querySelector('[data-sq-nav]'),
      back: root.querySelector('[data-sq-back]'),
      next: root.querySelector('[data-sq-next]'),
      submit: root.querySelector('[data-sq-submit]'),
      loading: root.querySelector('[data-sq-loading]'),
      loadingText: root.querySelector('[data-sq-loading-text]'),
      error: root.querySelector('[data-sq-error]'),
      errorRetry: root.querySelector('[data-sq-error-retry]'),
      emailCapture: root.querySelector('[data-sq-email-capture]'),
      results: root.querySelector('[data-sq-results]')
    };
  }

  // -------------------------------------------------------------------- state

  function freshState() {
    return {
      session_id: cryptoRandomId(),
      quiz_id: null,
      path: null,
      question_ids: ['name', 'confidence'],
      branch_owner_of: {},          // { question_id → owner_question_id } so we can prune cleanly
      current_index: 0,
      last_question_id: null,
      answers: {},
      started_at: new Date().toISOString(),
      pending_autosave: null,
      // Network state — not persisted
      _start_in_flight: false,
      _start_pending_queue: [],
      _autosave_failure_started_at: null,
      _start_done: false
    };
  }

  function cryptoRandomId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    // fallback
    return 'id-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  const PERSISTED_KEYS = [
    'session_id', 'quiz_id', 'path', 'question_ids', 'branch_owner_of',
    'current_index', 'last_question_id', 'answers', 'started_at',
    'pending_autosave'
  ];

  function persist(state) {
    try {
      const subset = {};
      PERSISTED_KEYS.forEach(function (k) { subset[k] = state[k]; });
      subset.version = 1;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(subset));
    } catch (e) {
      // localStorage can throw in private mode etc. — non-fatal
    }
  }

  function restore() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || parsed.version !== 1) return null;
      const fresh = freshState();
      PERSISTED_KEYS.forEach(function (k) {
        if (k in parsed) fresh[k] = parsed[k];
      });
      return fresh;
    } catch (e) {
      return null;
    }
  }

  function clearPersisted() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  // ----------------------------------------------------------------- realApi
  // Phase 3: hits the Railway-hosted FastAPI service. Exposes the same
  // { start, autosave, complete } surface as makeMockApi so the caller in
  // boot() can swap them transparently.

  function makeRealApi(cfg) {
    const base = (cfg.apiUrl || '').replace(/\/+$/, '');

    function post(path, payload) {
      return fetch(base + path, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (r) {
        if (!r.ok) {
          return r.json().catch(function () { return {}; }).then(function (body) {
            const err = new Error('API ' + path + ' failed: ' + r.status);
            err.status = r.status;
            err.detail = body && body.detail;
            throw err;
          });
        }
        // /autosave returns 204 No Content — guard against empty body parses.
        if (r.status === 204) return null;
        return r.json();
      });
    }

    return {
      start: function (payload) { return post('/quiz/start', payload); },
      autosave: function (quizId, payload) { return post('/quiz/' + encodeURIComponent(quizId) + '/autosave', payload); },
      complete: function (quizId, payload) { return post('/quiz/' + encodeURIComponent(quizId) + '/complete', payload); }
    };
  }

  // ----------------------------------------------------------------- mockApi

  function makeMockApi(cfg, answerRegistry, queryParams) {
    const fail = (queryParams.get('mock_fail') || '').split(',');

    function delay(ms, value) {
      return new Promise(function (resolve) { setTimeout(function () { resolve(value); }, ms); });
    }

    return {
      start: function (payload) {
        console.info('[quiz mock] POST /quiz/start', payload);
        if (fail.indexOf('start') !== -1) return delay(250).then(function () { throw new Error('mock /start failure'); });
        return delay(250, { quiz_id: 'stub-' + cryptoRandomId() });
      },
      autosave: function (quizId, payload) {
        console.info('[quiz mock] POST /quiz/' + quizId + '/autosave', payload);
        if (fail.indexOf('autosave') !== -1) return delay(150).then(function () { throw new Error('mock /autosave failure'); });
        return delay(150, null);
      },
      complete: function (quizId, payload) {
        console.info('[quiz mock] POST /quiz/' + quizId + '/complete', payload);
        if (fail.indexOf('complete') !== -1) return delay(1500).then(function () { throw new Error('mock /complete failure'); });
        const derived = deriveSymptomsFromAnswers(payload.answers || {}, answerRegistry);
        const hasSymptoms = Object.keys(derived.flags).length > 0;
        return delay(1500, {
          quiz_id: quizId,
          recommended_tot_band: 4,
          recommended_tot_cup: 5,
          recommended_display_band: '12',
          recommended_display_cup: 'C',
          confidence: hasSymptoms ? 0.82 : 0.95,
          symptoms_flags: derived.flags,
          symptoms_labels: derived.labels,
          placeholder_report: hasSymptoms
            ? 'Phase 2 placeholder — real report copy assembled in Phase 3 from symptoms_flags.'
            : 'Looks like your current size is working well for you. We didn’t detect any fit issues from your answers.'
        });
      }
    };
  }

  // Phase 2: derive symptom flags + human labels from raw answers, client-side.
  // Inputs are option UUIDs; the registry resolves UUID → slug so the rule
  // list stays human-readable. Phase 4 moves the authoritative derivation to
  // the Railway backend; this client copy stays so the mock (used for offline
  // dev when no api_url is set) keeps working.
  function deriveSymptomsFromAnswers(a, answerRegistry) {
    function slug(uuid) {
      if (uuid == null) return undefined;
      const entry = answerRegistry && answerRegistry.options && answerRegistry.options[uuid];
      return entry ? entry.slug : undefined;
    }
    const rules = [
      { when: slug(a.straps_dig_in) === 'painfully',                 flag: 'straps_dig_painfully',         label: 'Your straps are digging in painfully' },
      { when: slug(a.straps_dig_in) === 'slightly',                  flag: 'straps_dig_slightly',          label: 'Your straps leave marks on your shoulders by end of day' },
      { when: slug(a.straps_fall_off) === 'often',                   flag: 'straps_fall_off',              label: 'Your straps slip off your shoulders often' },
      { when: slug(a.band_behaviour) === 'moves_up',                 flag: 'band_moves_up',                label: 'Your band rides up your back' },
      { when: slug(a.band_behaviour) === 'digs_immediately',         flag: 'band_too_tight',               label: 'Your band feels too tight from the start' },
      { when: slug(a.band_behaviour) === 'digs_after_hours',         flag: 'band_tight_later',             label: 'Your band starts to dig in after a few hours' },
      { when: slug(a.band_position_eod) === 'above_blades',          flag: 'band_too_loose',               label: 'Your band rides up above your shoulder blades' },
      { when: slug(a.cups_fit_initial) === 'gaping_a_lot',           flag: 'cups_gape_initial',            label: 'Your cups gape a lot from the moment you put the bra on' },
      { when: slug(a.cups_fit_initial) === 'slight_gaping',          flag: 'cups_slight_gape_initial',     label: 'Your cups gape slightly from the moment you put the bra on' },
      { when: slug(a.cups_fit_initial) === 'slight_overflow',        flag: 'cups_slight_overflow_initial', label: 'Your cups overflow slightly from the moment you put the bra on' },
      { when: slug(a.cups_fit_initial) === 'overflowing_a_lot',      flag: 'cups_overflow_initial',        label: 'Your cups overflow a lot from the moment you put the bra on' },
      { when: slug(a.cups_change_during_day) === 'start_gaping',     flag: 'cups_gape_during_day',         label: 'Your cups become gappy as the day progresses' },
      { when: slug(a.cups_change_during_day) === 'gaping_worse',     flag: 'cups_gape_worse_during_day',   label: 'Your cups gape more as the day progresses' },
      { when: slug(a.cups_change_during_day) === 'breasts_fall_out', flag: 'cups_overflow_during_day',     label: 'Your breasts start to fall out of the cups as the day progresses' },
      { when: slug(a.wires_between_breasts) === 'slightly_off_chest',flag: 'wires_slight_gap_center',      label: 'The wires sit slightly off your sternum' },
      { when: slug(a.wires_between_breasts) === 'finger_or_more_off',flag: 'wires_gap_center',             label: 'The wires sit with a noticeable gap at your sternum' },
      { when: slug(a.wires_between_breasts) === 'dig_in_painfully',  flag: 'wires_dig_center',             label: 'The wires dig in painfully between your breasts' },
      { when: slug(a.wires_under_arms) === 'some_soft_tissue',       flag: 'tissue_above_wire',            label: 'Soft tissue spills over the top of the wire under your arms' },
      { when: slug(a.wires_under_arms) === 'dig_in_painfully',       flag: 'wires_dig_underarm',           label: 'The wires dig in painfully under your arms' },
      { when: slug(a.wires_under_breasts) === 'dont_touch',          flag: 'wires_held_off',               label: "The wires are held off your chest by your breasts (cups likely too small)" },
      { when: slug(a.wires_under_breasts) === 'dig_painfully',       flag: 'wires_dig_underbreast',        label: 'The wires dig in painfully under your breasts' },
      { when: slug(a.wires_moving_up) === 'often',                   flag: 'wires_moving_up',              label: 'The wires ride up during the day' },
      { when: slug(a.hook_position) === 'loosest',                   flag: 'on_loosest_hook',              label: "You're already on the loosest hook — band may be stretched" }
    ];
    const flags = {};
    const labels = {};
    rules.forEach(function (rule) {
      if (rule.when) {
        flags[rule.flag] = true;
        labels[rule.flag] = rule.label;
      }
    });
    return { flags: flags, labels: labels };
  }

  // -------------------------------------------------------------- the quiz

  function boot(root, els, cfg, registry, answerRegistry, queryParams, sizes) {
    const restored = restore();
    const state = restored || freshState();

    // Dev tool: drag the hotspot to position via `?hotspot=edit`. Activates
    // a floating bar with live coords + a "copy JSON" button that dumps all
    // current hotspot positions ready to paste into quiz-questions.json.
    const hotspotEditMode = queryParams.get('hotspot') === 'edit';
    const hotspotOverrides = {};
    let hotspotEditBar = null;

    // UUID → slug resolver. Returns undefined for free-form values (country
    // names in band/cup option lookups still use slugs; numerics like the
    // user-entered band size aren't in the registry at all).
    function slugForValue(value) {
      if (value == null) return undefined;
      const entry = answerRegistry && answerRegistry.options && answerRegistry.options[value];
      return entry ? entry.slug : undefined;
    }
    // Pick the real API when api_url is set on the section AND the merchant
    // hasn't asked for the mock via ?mock=1 / ?mock=true. Otherwise the Phase 2
    // mockApi runs entirely client-side and is still useful for offline dev.
    const wantsMock = (queryParams.get('mock') || '') === '1' || (queryParams.get('mock') || '').toLowerCase() === 'true';
    const useReal = !!cfg.apiUrl && !wantsMock;
    const mockApi = useReal ? makeRealApi(cfg) : makeMockApi(cfg, answerRegistry, queryParams);

    // Restore can leave us mid-question with last_question_id pointing to a
    // valid index inside question_ids — but if the registry shape changed,
    // bail and start over.
    if (restored && !validateRestored(state, registry)) {
      clearPersisted();
      Object.assign(state, freshState());
    }

    // Forced answers: the registry can pin certain questions to a fixed value
    // for questions that are hidden from the flow but still need to reach the
    // backend. Currently used to force has_measuring_tape → "no" while the
    // measurement pages are temporarily disabled (see registry forced_answers).
    // Applied at boot AND on restart (restartQuiz rebuilds state.answers).
    function applyForcedAnswers() {
      if (!registry.forced_answers) return;
      Object.keys(registry.forced_answers).forEach(function (qid) {
        if (state.answers[qid] == null) state.answers[qid] = registry.forced_answers[qid];
      });
    }
    applyForcedAnswers();

    function setIntro(visible) {
      if (els.intro) els.intro.hidden = !visible;
    }

    // True when the body figure should be visible for the active question.
    // Path-list membership (not just `state.path` truthiness) handles edge
    // cases where the path persists across a back-nav into an intro question.
    // Questions can opt out entirely with `hide_image: true` (utility /
    // non-anatomical questions like bra_brand, units, measurements).
    function shouldShowImage() {
      if (!state.path) return false;
      const q = activeQuestion();
      if (q && q.hide_image) return false;
      const pathQs = (registry.paths && registry.paths[state.path]) || [];
      return pathQs.indexOf(activeQuestionId()) !== -1;
    }

    function setStageView(view) {
      // view: 'questions' | 'loading' | 'email' | 'error' | 'results'
      els.stage.hidden = view !== 'questions';
      els.nav.hidden = view !== 'questions';
      els.progress.hidden = view !== 'questions';
      els.loading.hidden = view !== 'loading';
      els.error.hidden = view !== 'error';
      if (els.emailCapture) els.emailCapture.hidden = view !== 'email';
      els.results.hidden = view !== 'results';

      const onQuestions = view === 'questions';
      const prePath = onQuestions && !shouldShowImage();  // intro: name, confidence, wired_bra, wireless_with_cup
      const onLanding = onQuestions && state.current_index === 0;

      // Bra image only shows during the questions view AND when the active
      // question is inside one of the paths (country_size onward). Hidden
      // on intro questions, branch questions, loading, error, and results.
      if (els.imageWrap) els.imageWrap.hidden = view !== 'questions' || prePath;
      // Landing typography: only on the very first question
      root.classList.toggle('sizing-quiz--landing', onLanding);
      // Hide top-bar Start over link on the landing page (nothing to start over yet)
      if (els.progress) els.progress.hidden = !onQuestions || onLanding;

      setIntro(onQuestions && state.current_index === 0);

      // Section title only applies to question views — clear it for results/loading/error
      if (!onQuestions) updateSectionTitle(null);
    }

    // ------------------------------------------------------ question helpers

    function activeQuestionId() { return state.question_ids[state.current_index]; }
    function activeQuestion()   { return registry.questions[activeQuestionId()]; }
    function totalQuestions()   { return state.question_ids.length; }
    function isLast()           { return state.current_index === totalQuestions() - 1; }

    function interpolate(str) {
      if (!str) return '';
      return String(str).replace(/\{(\w+)\}/g, function (_, key) {
        return state.answers[key] != null ? String(state.answers[key]) : '';
      });
    }

    function optionsFor(question) {
      // Conditional option set: choose the list whose key matches the slug of
      // a prior answer (options_when_key). Used by cups_change_during_day to
      // show gaping- vs overflow- vs perfect-fit follow-ups based on the
      // cups_fit_initial slider. Returns [] until that prior answer is set.
      if (question.options_when) {
        const whenSlug = slugForValue(state.answers[question.options_when_key]);
        return (whenSlug && question.options_when[whenSlug]) || [];
      }
      if (question.options) return question.options;
      if (question.options_from) {
        const map = registry[question.options_from] || {};
        const key = state.answers[question.options_key];
        const list = map[key] || [];
        return list.map(function (v) { return { value: v, label: v }; });
      }
      return [];
    }

    // ---------------------------------------------------------- rendering

    function renderQuestion() {
      const q = activeQuestion();
      const id = activeQuestionId();
      els.stage.innerHTML = htmlForQuestion(q, id);

      // Restore previous answer into the freshly-rendered controls.
      const prev = state.answers[id];
      if (prev != null) writeAnswerToDOM(q, id, prev);
      // …and into any inline children rendered alongside.
      collectChildIds(q, id).forEach(function (cid) {
        const childQ = registry.questions && registry.questions[cid];
        const childPrev = state.answers[cid];
        if (childQ && childPrev != null) writeAnswerToDOM(childQ, cid, childPrev);
      });
      // Slider: initialise the value label on first render even when no
      // prior answer (the range default at the centre index).
      if (q && q.render_as === 'slider') updateSliderValueLabel(id);
      collectChildIds(q, id).forEach(function (cid) {
        const childQ = registry.questions && registry.questions[cid];
        if (childQ && childQ.render_as === 'slider') updateSliderValueLabel(cid);
      });

      attachStageListeners(q, id);
      updateNav();
      // Always run updateImage on every render. When shouldShowImage is
      // false (intro/branch questions), the figure wrap is display:none
      // so the call is visually inert, but we still want updateHotspot
      // and updateSkinTone to reset state cleanly (otherwise stale mask
      // colour / hotspot position carry across navigations).
      updateImage();
      // Re-evaluate landing / intro / image visibility for the new question.
      // setStageView() only fires when switching between question/loading/error
      // /results views; navigation within `questions` view still needs to
      // update these.
      if (els.imageWrap) els.imageWrap.hidden = !shouldShowImage();
      root.classList.toggle('sizing-quiz--landing', state.current_index === 0);
      if (els.progress) els.progress.hidden = state.current_index === 0;
      setIntro(state.current_index === 0);
      updateSectionTitle(q);
      // Populate any measurement echo with the restored answer on first render
      // (subsequent typing updates via stageChanged → updateMeasureEchoes).
      updateMeasureEchoes();
    }

    function updateSectionTitle(q) {
      if (!els.sectionTitle) return;
      // Persistence: only update when the active question DECLARES a
      // section_title. Otherwise leave whatever the previous question
      // set in place. Matches the live quiz where a chapter heading
      // ("Let's talk about your straps.") stays put through all the
      // questions of that section. Pass q = null to force-clear (used
      // when switching to loading/results/error views).
      if (q == null) {
        els.sectionTitle.textContent = '';
        els.sectionTitle.hidden = true;
        return;
      }
      if (!q.section_title) return; // inherit previous
      const title = interpolate(q.section_title);
      if (title) {
        els.sectionTitle.textContent = title;
        els.sectionTitle.hidden = false;
      }
    }

    function htmlForQuestion(q, id) {
      const legend = escapeHtml(interpolate(q.legend || ''));
      const preamble = q.preamble ? '<p class="sizing-quiz__preamble">' + escapeHtml(interpolate(q.preamble)) + '</p>' : '';
      const footnote = q.footnote ? '<p class="sizing-quiz__footnote">' + escapeHtml(interpolate(q.footnote)) + '</p>' : '';

      let body = '';
      switch (q.type) {
        case 'text':
        case 'email':
          body = htmlTextInput(q, id);
          break;
        case 'number':
          body = htmlNumberInput(q, id);
          break;
        case 'single_select':
          // Per-question render mode — `render_as: "slider"` swaps the
          // radio list for a horizontal range slider with endpoint
          // labels (used for cups_fit_initial in the live quiz).
          body = q.render_as === 'slider'
            ? htmlSliderSelect(q, id)
            : htmlSingleSelect(q, id);
          break;
        case 'country_size':
          body = htmlCountrySize(q, id);
          break;
        case 'multi_select':
          body = htmlMultiSelect(q, id);
          break;
        case 'swatch_select':
          body = htmlSwatchSelect(q, id);
          break;
        default:
          body = '<p>Unknown question type: ' + escapeHtml(q.type || 'undefined') + '</p>';
      }

      let html = '<legend class="sizing-quiz__legend">' + legend + '</legend>' +
        preamble +
        body +
        footnote;

      // Inline children: render any always-visible (children) or
      // conditionally-visible (children_when[selectedSlug]) sub-questions
      // immediately below this one on the same page. We render the
      // container UNCONDITIONALLY whenever the question declares either
      // field — even empty — so stageChanged has a stable element to
      // update when the parent's answer flips. The data-sq-children-for
      // attr ties each container to its parent question id so stageChanged
      // can target the right nesting level.
      if (Array.isArray(q.children) || q.children_when) {
        const childIds = collectChildIds(q, id);
        html += '<div class="sizing-quiz__children" data-sq-children data-sq-children-for="' + escapeAttr(id) + '">' +
          childIds.map(function (cid) {
            const childQ = registry.questions && registry.questions[cid];
            if (!childQ) return '';
            return '<fieldset class="sizing-quiz__child" data-sq-child-id="' + escapeAttr(cid) + '">' +
                   htmlForQuestion(childQ, cid) +
                   '</fieldset>';
          }).join('') +
        '</div>';
      }

      return html;
    }

    // Children resolved from a question's schema:
    //   children:       always rendered (e.g. units below has_measuring_tape)
    //   children_when:  keyed by parent answer slug → list of child ids
    function collectChildIds(q, parentId) {
      const ids = [];
      if (Array.isArray(q.children)) {
        q.children.forEach(function (c) { if (ids.indexOf(c) === -1) ids.push(c); });
      }
      if (q.children_when) {
        const ans = state.answers[parentId];
        const slug = ans ? slugForValue(ans) : null;
        if (slug && Array.isArray(q.children_when[slug])) {
          q.children_when[slug].forEach(function (c) { if (ids.indexOf(c) === -1) ids.push(c); });
        }
      }
      return ids;
    }

    function htmlTextInput(q, id) {
      const type = q.type === 'email' ? 'email' : 'text';
      const labelText = q.placeholder || '';
      const label = labelText
        ? '<label class="sizing-quiz__field-label" for="' + escapeAttr(id) + '">' + escapeHtml(labelText) + '</label>'
        : '';
      return '<div class="sizing-quiz__field sizing-quiz__field--underline">' +
        label +
        '<input class="sizing-quiz__field-input" id="' + escapeAttr(id) + '" type="' + type + '" name="' + escapeAttr(id) + '" autocomplete="off">' +
      '</div>';
    }

    // Resolve unit-aware min/max for a number question. Picks the cm or
    // inches pair based on the answer to `unit_from_answer` (resolved via
    // the answer registry). Falls back to the legacy single min/max if the
    // question still uses the old schema, then to {} if neither is set.
    function unitAwareRange(q) {
      const unitsSlug = q.unit_from_answer ? slugForValue(state.answers[q.unit_from_answer]) : undefined;
      if (unitsSlug === 'cm' && (q.min_cm != null || q.max_cm != null)) {
        return { min: q.min_cm, max: q.max_cm };
      }
      if (unitsSlug === 'inches' && (q.min_in != null || q.max_in != null)) {
        return { min: q.min_in, max: q.max_in };
      }
      if (q.min != null || q.max != null) {
        return { min: q.min, max: q.max };
      }
      return {};
    }

    function htmlNumberInput(q, id) {
      const range = unitAwareRange(q);
      const min = range.min != null ? ' min="' + range.min + '"' : '';
      const max = range.max != null ? ' max="' + range.max + '"' : '';
      const labelText = q.placeholder || '';
      const label = labelText
        ? '<label class="sizing-quiz__field-label" for="' + escapeAttr(id) + '">' + escapeHtml(labelText) + '</label>'
        : '';
      // No inline error/tip text — invalid input just keeps Next disabled
      // (via isCurrentAnswerValid). Out-of-range values pass through and
      // are silently dropped by the API failsafe.
      // Big echo display above the input ("30″") — populated by
      // stageChanged via [data-sq-measure-echo] / [data-sq-measure-for].
      return '<div class="sizing-quiz__field sizing-quiz__field--underline sizing-quiz__field--measure">' +
        '<span class="sizing-quiz__measure-echo" data-sq-measure-echo data-sq-measure-for="' + escapeAttr(id) + '" aria-hidden="true"></span>' +
        label +
        '<input class="sizing-quiz__field-input" id="' + escapeAttr(id) + '" type="number" inputmode="decimal" step="any"' +
          ' name="' + escapeAttr(id) + '"' + min + max + '>' +
      '</div>';
    }

    // Horizontal slider rendering for single_select questions tagged
    // `render_as: "slider"`. The slider's value is the option index;
    // the option's UUID lands in state.answers via input -> commit. The
    // currently-selected option's label appears above the track; the
    // first + last option labels anchor the ends.
    function htmlSliderSelect(q, id) {
      const opts = optionsFor(q);
      const n = opts.length;
      if (n === 0) return '';
      const startIdx = Math.floor((n - 1) / 2);
      return '<div class="sizing-quiz__slider" data-sq-slider data-sq-slider-id="' + escapeAttr(id) + '">' +
        '<p class="sizing-quiz__slider-value" data-sq-slider-value></p>' +
        '<input class="sizing-quiz__slider-input" type="range" name="' + escapeAttr(id) + '"' +
          ' min="0" max="' + (n - 1) + '" step="1" value="' + startIdx + '"' +
          ' data-sq-slider-input' +
          ' aria-valuemin="0" aria-valuemax="' + (n - 1) + '" aria-valuenow="' + startIdx + '">' +
        '<div class="sizing-quiz__slider-labels">' +
          '<span>' + escapeHtml(opts[0].label || '') + '</span>' +
          '<span>' + escapeHtml(opts[n - 1].label || '') + '</span>' +
        '</div>' +
        // Hidden option values, indexed → looked up by writeAnswerToDOM
        opts.map(function (o, i) {
          return '<input type="hidden" data-sq-slider-option="' + i + '" value="' + escapeAttr(o.value) + '">';
        }).join('') +
      '</div>';
    }

    function htmlSingleSelect(q, id) {
      const opts = optionsFor(q);
      const cls = decideOptionsLayoutClass(q, id, opts);
      return '<ul class="sizing-quiz__options ' + cls + '">' +
        opts.map(function (o) {
          return '<li><label class="sizing-quiz__option">' +
            '<input type="radio" name="' + escapeAttr(id) + '" value="' + escapeAttr(o.value) + '">' +
            '<span class="sizing-quiz__option-label">' + escapeHtml(o.label || o.value) + '</span>' +
            '</label></li>';
        }).join('') + '</ul>';
    }

    function htmlMultiSelect(q, id) {
      const opts = optionsFor(q);
      return '<ul class="sizing-quiz__options">' +
        opts.map(function (o) {
          return '<li><label class="sizing-quiz__option">' +
            '<input type="checkbox" name="' + escapeAttr(id) + '" value="' + escapeAttr(o.value) + '">' +
            '<span class="sizing-quiz__option-label">' + escapeHtml(o.label || o.value) + '</span>' +
            '</label></li>';
        }).join('') + '</ul>';
    }

    function htmlCountrySize(q, id) {
      const countryOpts = q.country_options || [];
      const tilesHtml = countryOpts.map(function (o) {
        // o.value is the UUID; flag URLs are keyed by the original slug
        // because they come from section data-* attributes the merchant
        // configured by country name.
        const flagUrl = cfg.flagUrls[o.slug];
        const flagImg = flagUrl
          ? '<img class="sizing-quiz__country-flag" src="' + escapeAttr(flagUrl) + '" alt="" loading="lazy">'
          : '';
        return '<li><label class="sizing-quiz__country">' +
          '<input type="radio" name="' + escapeAttr(id) + '__country" value="' + escapeAttr(o.value) + '">' +
          flagImg +
          '<span class="sizing-quiz__country-label">' + escapeHtml(o.label || o.slug) + '</span>' +
          '</label></li>';
      }).join('');

      return '' +
        '<ul class="sizing-quiz__options sizing-quiz__options--countries">' + tilesHtml + '</ul>' +
        '<div class="sizing-quiz__composite-step" data-sq-step-2 hidden>' +
          '<h3 class="sizing-quiz__composite-step-legend">' + escapeHtml(q.step2_legend || 'What size is it?') + '</h3>' +
          '<div class="sizing-quiz__field">' +
            '<label class="sizing-quiz__field-label" for="' + escapeAttr(id) + '__band">' + escapeHtml(q.band_label || 'Band size') + '</label>' +
            '<select class="sizing-quiz__field-input" id="' + escapeAttr(id) + '__band" name="' + escapeAttr(id) + '__band">' +
              '<option value="">Select…</option>' +
            '</select>' +
          '</div>' +
          '<div class="sizing-quiz__field" data-sq-cup-field hidden>' +
            '<label class="sizing-quiz__field-label" for="' + escapeAttr(id) + '__cup">' + escapeHtml(q.cup_label || 'Cup size') + '</label>' +
            '<select class="sizing-quiz__field-input" id="' + escapeAttr(id) + '__cup" name="' + escapeAttr(id) + '__cup">' +
              '<option value="">Select…</option>' +
            '</select>' +
          '</div>' +
        '</div>';
    }

    function htmlSwatchSelect(q, id) {
      const opts = optionsFor(q);
      return '<ul class="sizing-quiz__options sizing-quiz__options--swatches">' +
        opts.map(function (o) {
          return '<li><label class="sizing-quiz__swatch" style="--swatch:' + escapeAttr(o.swatch || '#ccc') + '">' +
            '<input type="radio" name="' + escapeAttr(id) + '" value="' + escapeAttr(o.value) + '">' +
            '<span class="sizing-quiz__swatch-disc"></span>' +
            '<span>' + escapeHtml(o.label || o.value) + '</span>' +
            '</label></li>';
        }).join('') + '</ul>';
    }

    function decideOptionsLayoutClass(q, id, opts) {
      if (id === 'band' || id === 'cup') return 'sizing-quiz__options--grid';
      return '';
    }

    // ---------------------------------------------------- DOM read/write

    function readAnswerFromDOM(q, id) {
      switch (q.type) {
        case 'text':
        case 'email': {
          const el = els.stage.querySelector('input[name="' + cssEscape(id) + '"]');
          return el ? el.value.trim() : '';
        }
        case 'number': {
          const el = els.stage.querySelector('input[name="' + cssEscape(id) + '"]');
          if (!el || el.value === '') return null;
          const n = Number(el.value);
          return Number.isFinite(n) ? n : null;
        }
        case 'single_select': {
          if (q.render_as === 'slider') {
            const range = els.stage.querySelector('input[type="range"][name="' + cssEscape(id) + '"]');
            if (!range) return null;
            const idx = Number(range.value);
            const opt = els.stage.querySelector('[data-sq-slider-option="' + idx + '"]');
            return opt ? opt.value : null;
          }
          const checked = els.stage.querySelector('input[name="' + cssEscape(id) + '"]:checked');
          return checked ? checked.value : null;
        }
        case 'swatch_select': {
          const checked = els.stage.querySelector('input[name="' + cssEscape(id) + '"]:checked');
          return checked ? checked.value : null;
        }
        case 'multi_select': {
          const checked = els.stage.querySelectorAll('input[name="' + cssEscape(id) + '"]:checked');
          return Array.from(checked).map(function (i) { return i.value; });
        }
        case 'country_size': {
          const checkedCountry = els.stage.querySelector('input[name="' + cssEscape(id) + '__country"]:checked');
          const bandSelect = els.stage.querySelector('select[name="' + cssEscape(id) + '__band"]');
          const cupSelect  = els.stage.querySelector('select[name="' + cssEscape(id) + '__cup"]');
          return {
            country: checkedCountry ? checkedCountry.value : null,
            band:    bandSelect ? (bandSelect.value || null) : null,
            cup:     cupSelect ? (cupSelect.value || null) : null
          };
        }
      }
      return null;
    }

    function writeAnswerToDOM(q, id, value) {
      switch (q.type) {
        case 'text':
        case 'email':
        case 'number': {
          const el = els.stage.querySelector('input[name="' + cssEscape(id) + '"]');
          if (el) el.value = value == null ? '' : value;
          return;
        }
        case 'single_select': {
          if (q.render_as === 'slider') {
            const opts = optionsFor(q);
            const idx = opts.findIndex(function (o) { return o.value === value; });
            if (idx >= 0) {
              const range = els.stage.querySelector('input[type="range"][name="' + cssEscape(id) + '"]');
              if (range) range.value = String(idx);
            }
            updateSliderValueLabel(id);
            return;
          }
          const el = els.stage.querySelector('input[name="' + cssEscape(id) + '"][value="' + cssEscape(String(value)) + '"]');
          if (el) el.checked = true;
          return;
        }
        case 'swatch_select': {
          const el = els.stage.querySelector('input[name="' + cssEscape(id) + '"][value="' + cssEscape(String(value)) + '"]');
          if (el) el.checked = true;
          return;
        }
        case 'multi_select': {
          const set = new Set(Array.isArray(value) ? value.map(String) : []);
          const all = els.stage.querySelectorAll('input[name="' + cssEscape(id) + '"]');
          all.forEach(function (el) { el.checked = set.has(el.value); });
          return;
        }
        // country_size restores via attachCountrySizeListeners
      }
    }

    // Refresh the "currently selected" label above the slider track.
    // Called from writeAnswerToDOM and slider input change handler.
    function updateSliderValueLabel(id) {
      const slider = els.stage.querySelector('[data-sq-slider-id="' + cssEscape(id) + '"]');
      if (!slider) return;
      const range = slider.querySelector('[data-sq-slider-input]');
      const valueEl = slider.querySelector('[data-sq-slider-value]');
      if (!range || !valueEl) return;
      const idx = Number(range.value);
      const opt = slider.querySelector('[data-sq-slider-option="' + idx + '"]');
      // We don't track labels on the hidden inputs — pull from the
      // question's options.
      const qid = id;
      const q = registry.questions[qid];
      const opts = optionsFor(q);
      if (opts[idx]) valueEl.textContent = interpolate(opts[idx].label || '');
      range.setAttribute('aria-valuenow', String(idx));
    }

    // Validates the main question + any visible children (recursively)
    // for the active page. Lets nested inline questions (wired_bra →
    // wireless_with_cup → proceed_method) all gate the Next button.
    function isCurrentAnswerValid() {
      const q = activeQuestion();
      const id = activeQuestionId();
      return walkVisibleAllValid(q, id);
    }
    function walkVisibleAllValid(q, id) {
      if (!isOneQuestionValid(q, id)) return false;
      const childIds = collectChildIds(q, id);
      for (let i = 0; i < childIds.length; i++) {
        const cid = childIds[i];
        const childQ = registry.questions && registry.questions[cid];
        if (childQ && !walkVisibleAllValid(childQ, cid)) return false;
      }
      return true;
    }

    function isOneQuestionValid(q, id) {
      if (!q) return false;
      const val = readAnswerFromDOM(q, id);
      switch (q.type) {
        case 'text':
          return q.validate === 'non_empty' ? !!(val && val.length) : true;
        case 'email':
          return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val || '');
        case 'number':
          return val != null;
        case 'single_select':
        case 'swatch_select':
          return val != null;
        case 'multi_select':
          return Array.isArray(val) && val.length > 0;
        case 'country_size':
          return !!(val && val.country && val.band && val.cup);
      }
      return false;
    }

    // ----------------------------------------------------- routing + branch

    function applyRoutingFromCommit(q, id, value) {
      // Routing keys (routes_path, branches) are kept as slugs in
      // quiz-questions.json for human readability; the answer value on the
      // wire is a UUID. Resolve UUID → slug here so the lookups still hit.
      const slug = slugForValue(value);

      // 1. routes_path
      if (q.routes_path && slug != null && q.routes_path[slug]) {
        const pathName = q.routes_path[slug];
        if (state.path !== pathName) {
          // Switching paths: drop any future questions that came from
          // a previously-routed path (and aren't in the new path). Keeps
          // the user from advancing into the wrong path's queue after a
          // back-nav + re-answer (e.g. picking no_bra first then
          // switching to wireless via proceed_method).
          const newPathSet = {};
          (registry.paths[pathName] || []).forEach(function (qid) { newPathSet[qid] = true; });
          const otherPathSet = {};
          Object.keys(registry.paths || {}).forEach(function (pname) {
            if (pname === pathName) return;
            (registry.paths[pname] || []).forEach(function (qid) {
              if (!newPathSet[qid]) otherPathSet[qid] = true;
            });
          });
          state.question_ids = state.question_ids.filter(function (qid, idx) {
            if (idx <= state.current_index) return true; // keep current + past
            return !otherPathSet[qid]; // drop future questions exclusive to other paths
          });
          state.path = pathName;
          (registry.paths[pathName] || []).forEach(function (qid) {
            if (state.question_ids.indexOf(qid) === -1) state.question_ids.push(qid);
          });
        }
      }

      // 2. branches — splice in immediately after current index, tag owner
      if (q.branches && slug != null && q.branches[slug]) {
        const branchIds = q.branches[slug];
        const insertAt = state.current_index + 1;
        // Strip out any prior branch from this owner first (so changing branches works)
        pruneBranchesOwnedBy(id);
        branchIds.forEach(function (bid, i) {
          if (state.question_ids.indexOf(bid) === -1) {
            state.question_ids.splice(insertAt + i, 0, bid);
          }
          state.branch_owner_of[bid] = id;
        });
      } else if (q.branches) {
        // Owner answered, but the new value triggers no branches — prune any
        // questions previously inserted by this owner.
        pruneBranchesOwnedBy(id);
      }
    }

    function pruneBranchesOwnedBy(ownerId) {
      const toRemove = [];
      Object.keys(state.branch_owner_of).forEach(function (qid) {
        if (state.branch_owner_of[qid] === ownerId) toRemove.push(qid);
      });
      toRemove.forEach(function (qid) {
        const idx = state.question_ids.indexOf(qid);
        if (idx > -1) state.question_ids.splice(idx, 1);
        delete state.branch_owner_of[qid];
        delete state.answers[qid];
      });
    }

    // -------------------------------------------------------- navigation

    function commitCurrent() {
      const q = activeQuestion();
      const id = activeQuestionId();
      const val = readAnswerFromDOM(q, id);
      if (q.type === 'country_size' && val && typeof val === 'object') {
        state.answers.country = val.country;
        state.answers.band = val.band;
        state.answers.cup = val.cup;
      } else {
        state.answers[id] = val;
      }
      // Persist every visible child (recursively) + apply their routing.
      // Skip routing for a child whose ancestor already triggered a
      // routes_path — parent's path wins. Walks the visible tree so
      // grandchildren (e.g. proceed_method under wireless_with_cup) get
      // their answers + routing applied too.
      const parentSlug = slugForValue(val);
      const parentRouted = !!(q.routes_path && parentSlug && q.routes_path[parentSlug]);
      function commitVisibleChildren(parentQ, parentId, ancestorRouted) {
        const cids = collectChildIds(parentQ, parentId);
        cids.forEach(function (cid) {
          const childQ = registry.questions && registry.questions[cid];
          if (!childQ) return;
          const cv = readAnswerFromDOM(childQ, cid);
          state.answers[cid] = cv;
          let nowRouted = ancestorRouted;
          if (cv != null && !ancestorRouted) {
            applyRoutingFromCommit(childQ, cid, cv);
            const cSlug = slugForValue(cv);
            if (childQ.routes_path && cSlug && childQ.routes_path[cSlug]) {
              nowRouted = true;
            }
          }
          commitVisibleChildren(childQ, cid, nowRouted);
        });
      }
      commitVisibleChildren(q, id, parentRouted);
      state.last_question_id = id;
      applyRoutingFromCommit(q, id, val);
      if (id === 'cup' || id === 'country' || id === 'band' || id === 'skin_tone' || q.type === 'country_size') updateImage();
    }

    function next() {
      if (!isCurrentAnswerValid()) return;
      commitCurrent();
      kickStart();
      kickAutosave();
      persist(state);

      if (state.current_index < state.question_ids.length - 1) {
        state.current_index += 1;
        renderQuestion();
        persist(state);
        scrollSectionToView();
      }
    }

    function back() {
      if (state.current_index === 0) return;
      // commit any partial answer (don't validate) so going back/forward keeps it
      const q = activeQuestion();
      const id = activeQuestionId();
      const val = readAnswerFromDOM(q, id);
      if (val !== null && val !== '' && !(Array.isArray(val) && val.length === 0)) {
        state.answers[id] = val;
      }
      state.current_index -= 1;
      renderQuestion();
      persist(state);
      scrollSectionToView();
    }

    // After a Next/Back nav, scroll the viewport all the way to the page
    // top (not just the section top, which my first pass did — that
    // landed below the host theme's header band, which read as "scrolling
    // somewhere but not to top"). Use window.scrollTo with explicit 0,
    // belt-and-braces with document.scrollingElement so it works whether
    // the document or body is the scroll container.
    function scrollSectionToView() {
      requestAnimationFrame(function () {
        try {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        } catch (_) {
          window.scrollTo(0, 0);
        }
        // Belt-and-braces: some themes wrap content in their own scroll
        // containers; jab those to 0 too.
        if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
        if (document.documentElement) document.documentElement.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;
      });
    }

    function submit() {
      if (!isCurrentAnswerValid()) return;
      commitCurrent();
      persist(state);
      // Gate the report behind the email-capture step. The user either
      // submits an email (→ discount code on the report) or skips. Both
      // paths land in kickComplete() from within showEmailCapture().
      showEmailCapture();
    }

    // For every visible measurement input that has a `[data-sq-measure-echo]`
    // sibling, mirror the typed value into the echo span. Renders large
    // above the input so the user sees the value they're entering — same
    // pattern as the "30 ″" treatment in the design prototype.
    function updateMeasureEchoes() {
      const echoes = els.stage ? els.stage.querySelectorAll('[data-sq-measure-echo]') : [];
      for (let i = 0; i < echoes.length; i++) {
        const echo = echoes[i];
        const targetId = echo.getAttribute('data-sq-measure-for');
        if (!targetId) continue;
        const input = els.stage.querySelector('input[name="' + cssEscape(targetId) + '"]');
        if (!input) continue;
        const v = (input.value || '').trim();
        echo.textContent = v;
      }
    }

    function updateNav() {
      els.back.hidden = state.current_index === 0;

      // "Effectively last" = last in the current list AND the current answer
      // wouldn't extend the list via routes_path or branches. This stops the
      // submit button from appearing on path-determining questions like
      // `confidence` (which is last in the initial [name, confidence] list,
      // but its answer always adds more questions).
      const effectivelyLast = isLast() && !currentAnswerWouldExtend();
      els.next.hidden = effectivelyLast;
      els.submit.hidden = !effectivelyLast;

      const valid = isCurrentAnswerValid();
      els.next.disabled = !valid;
      els.submit.disabled = !valid;
    }

    function currentAnswerWouldExtend() {
      const q = activeQuestion();
      const id = activeQuestionId();
      // Walk the active question + any visible children. If ANY one of
      // them has an answer that triggers routes_path or branches, the
      // submit button stays hidden until that routing has fired. Without
      // this, picking proceed_method (a nested child of wired_bra) had
      // no effect on wired_bra's own routing — the quiz thought we were
      // done and showed Submit → straight to results.
      function questionAnswerExtends(qq, qid) {
        const v = readAnswerFromDOM(qq, qid);
        if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) {
          if (qq.routes_path || qq.branches) return true; // could extend once answered
          return false;
        }
        const slug = slugForValue(v);
        if (slug != null && qq.routes_path && qq.routes_path[slug]) return true;
        if (slug != null && qq.branches && qq.branches[slug]) return true;
        return false;
      }
      function walkExtends(qq, qid) {
        if (questionAnswerExtends(qq, qid)) return true;
        const cids = collectChildIds(qq, qid);
        for (let i = 0; i < cids.length; i++) {
          const childQ = registry.questions && registry.questions[cids[i]];
          if (childQ && walkExtends(childQ, cids[i])) return true;
        }
        return false;
      }
      return walkExtends(q, id);
    }

    // -------------------------------------------------------- image swap

    // Map answer values into TOT coords using the /sizes payload, then snap
    // to the nearest-lower anchor (one of FIGURE_BAND_ANCHORS /
    // FIGURE_CUP_ANCHORS) so we can serve the right of 12 figures.
    //
    // Falls back to null when:
    //   - the /sizes fetch didn't succeed
    //   - any of country / band / cup are missing
    //   - the (band, cup) pair doesn't map onto a row in country.sizes
    // null → callers keep showing whatever's currently on screen.
    function figureUrlFromAnswers() {
      const q = activeQuestion();
      // 1. Per-option override: if the active question's currently-
      //    selected option carries a `figure` field, use it. Lets a
      //    radio answer swap the figure live (band_position_eod) and
      //    will also drive the slider on cups_fit_initial.
      const optionFig = selectedOptionAsset(q, 'figure');
      if (optionFig) return cfg.figureVariants[optionFig] || null;
      // 2. Question-level view override.
      if (q && q.view === 'back') return cfg.figureVariants.back || null;
      // 3. TOT-anchored front-view lookup.
      if (!sizes || !sizes.countries) return null;
      const tableSlug = countryTableSlug(slugForValue(state.answers.country));
      if (!tableSlug) return null;
      const country = sizes.countries[tableSlug];
      if (!country) return null;

      // Band-aware joint lookup: cup meaning depends on band (sister sizing),
      // so both coords come from the single (band, cup) row — never a cup
      // resolved on its own.
      const tot = lookupTotFromSize(country, state.answers.band, state.answers.cup);
      if (!tot) return null;

      const bandAnchor = nearestLowerAnchor(tot.totBand, FIGURE_BAND_ANCHORS);
      const cupAnchor  = nearestLowerAnchor(tot.totCup,  FIGURE_CUP_ANCHORS);
      const key = 'tot' + bandAnchor + '-tot' + cupAnchor;
      return cfg.figures[key] || null;
    }

    // Mirror of _country_table_suffix() in fit-check-api/app/recommend.py:
    //   "AU/NZ" → "au_nz"
    //   "USA"   → "usa" → "us"
    //   "Italy" → "italy"
    // …etc. Lowercase, swap "/" and " " for "_", then special-case usa→us.
    function countryTableSlug(registrySlug) {
      if (!registrySlug) return null;
      const safe = String(registrySlug).toLowerCase().replace(/\//g, '_').replace(/ /g, '_');
      return safe === 'usa' ? 'us' : safe;
    }

    // native (band, cup) -> { totBand, totCup } via the joint sizes matrix.
    // Band-aware: the same cup letter means different things at different
    // bands (sister sizing), so we match the FULL (band, cup) row — never a
    // cup by tot_cup or by itself. Cup labels are matched as exact strings so
    // slashes (US "DD/E", "DDD/F", "DDDD/G") compare literally.
    function lookupTotFromSize(country, band, cup) {
      const rows = country && country.sizes;
      if (!Array.isArray(rows) || band == null || band === '' || cup == null || cup === '') return null;
      const b = String(band);
      const c = String(cup);
      for (let i = 0; i < rows.length; i++) {
        if (String(rows[i].country_band) === b && String(rows[i].country_cup) === c) {
          return { totBand: rows[i].tot_band, totCup: rows[i].tot_cup };
        }
      }
      return null;
    }

    // band alone -> tot_band. tot_band is a pure function of the band, so the
    // distinct-bands array resolves it without a cup. Band ranges differ by
    // country (e.g. France's smallest band is tot_band 2, not 0) — a miss
    // returns null rather than assuming a shared range.
    function lookupTotBand(country, band) {
      const rows = country && country.bands;
      if (!Array.isArray(rows) || band == null || band === '') return null;
      const b = String(band);
      for (let i = 0; i < rows.length; i++) {
        if (String(rows[i].country_band) === b) return rows[i].tot_band;
      }
      return null;
    }

    function nearestLowerAnchor(value, anchors) {
      let pick = anchors[0];
      for (let i = 0; i < anchors.length; i++) {
        if (anchors[i] <= value) pick = anchors[i];
      }
      return pick;
    }

    function updateImage() {
      updateSkinTone();
      updateHotspot();
      if (!els.image) return;
      const target = figureUrlFromAnswers() || cfg.images.default;
      const curBase = (els.image.getAttribute('src') || '').split('?')[0];
      const tgtBase = (target || '').split('?')[0];
      if (!target || curBase === tgtBase) return;

      els.image.classList.add('is-fading');
      setTimeout(function () {
        els.image.setAttribute('src', target);
        els.image.classList.remove('is-fading');
      }, 200);
    }

    // Place the anatomy hotspot (red dot) over the body figure at the
    // active question's configured (x, y) in 0..480 viewBox coords.
    // Hidden when the active question has no `hotspot` field or when the
    // figure itself isn't visible (shouldShowImage()).
    function updateHotspot() {
      if (!els.hotspot) return;
      const qid = activeQuestionId();
      const hs = qid ? getCurrentHotspot(qid) : null;
      // No auto-show in edit mode any more — the user has to click "+ add
      // here" to author a new hotspot. Prevents accidental "I just clicked
      // through the question" drag overrides on non-anatomical pages.
      if (!hs || !shouldShowImage()) {
        els.hotspot.hidden = true;
        if (hotspotEditMode) updateHotspotEditBar();
        return;
      }
      const xPct = (Number(hs.x) / 480) * 100;
      const yPct = (Number(hs.y) / 480) * 100;
      els.hotspot.style.left = xPct + '%';
      els.hotspot.style.top  = yPct + '%';
      els.hotspot.hidden = false;
      if (hotspotEditMode) updateHotspotEditBar();
    }

    // Returns the value of `field` (e.g. 'figure', 'skin_tone') on the
    // currently-selected option of `q`, or null if there's no selection
    // or that option doesn't carry the field. Used by figure / skin-tone
    // resolvers so a radio pick can swap visuals live.
    function selectedOptionAsset(q, field) {
      if (!q || !q.options || !Array.isArray(q.options)) return null;
      const qid = activeQuestionId();
      const selected = qid && state.answers[qid];
      if (!selected) return null;
      for (let i = 0; i < q.options.length; i++) {
        const opt = q.options[i];
        if (opt && opt.value === selected) return opt[field] || null;
      }
      return null;
    }

    // Returns the active hotspot for `qid`, honouring overrides:
    //   - override is {x, y}  → use it
    //   - override is null    → explicitly removed (no hotspot)
    //   - no override         → fall back to registry.questions[qid].hotspot
    function getCurrentHotspot(qid) {
      if (Object.prototype.hasOwnProperty.call(hotspotOverrides, qid)) {
        return hotspotOverrides[qid];
      }
      const q = registry.questions && registry.questions[qid];
      return (q && q.hotspot) || null;
    }

    // -------- Hotspot edit mode (dev tool gated by ?hotspot=edit) --------

    function updateHotspotEditBar() {
      if (!hotspotEditBar) return;
      const qid = activeQuestionId();
      const curr = qid ? getCurrentHotspot(qid) : null;
      const overridden = qid && Object.prototype.hasOwnProperty.call(hotspotOverrides, qid);
      const mark = overridden ? ' *' : '';
      const span = hotspotEditBar.querySelector('[data-bar-text]');
      if (span) {
        span.textContent = qid
          ? qid + ': ' + (curr ? '(' + Math.round(curr.x) + ', ' + Math.round(curr.y) + ')' + mark : 'no hotspot')
          : 'edit mode';
      }
      // Add / Remove are mutually exclusive based on whether the active
      // question currently has a hotspot.
      const addBtn    = hotspotEditBar.querySelector('[data-bar-add]');
      const removeBtn = hotspotEditBar.querySelector('[data-bar-remove]');
      if (addBtn)    addBtn.hidden    = !!curr || !qid || !shouldShowImage();
      if (removeBtn) removeBtn.hidden = !curr;
    }

    function initHotspotEditMode() {
      if (!hotspotEditMode) return;
      root.dataset.sqHotspotEdit = '1';

      // Floating bar with live coords + add / remove / copy controls.
      hotspotEditBar = document.createElement('div');
      hotspotEditBar.className = 'sq-hotspot-edit-bar';
      hotspotEditBar.innerHTML =
        '<span data-bar-text>edit mode</span>' +
        '<button type="button" data-bar-add hidden>+ add here</button>' +
        '<button type="button" data-bar-remove hidden>× remove</button>' +
        '<button type="button" data-bar-copy>copy JSON</button>';
      document.body.appendChild(hotspotEditBar);

      // "+ add here": author a new hotspot at viewBox centre so the user
      // can drag it into place. Hidden when the question already has one.
      hotspotEditBar.querySelector('[data-bar-add]').addEventListener('click', function () {
        const qid = activeQuestionId();
        if (!qid) return;
        hotspotOverrides[qid] = { x: 240, y: 240 };
        updateHotspot();
      });

      // "× remove": flag the active question as having no hotspot. Null
      // sentinel in the overrides map distinguishes "explicitly removed"
      // from "never had one", so the JSON output omits removed entries.
      hotspotEditBar.querySelector('[data-bar-remove]').addEventListener('click', function () {
        const qid = activeQuestionId();
        if (!qid) return;
        hotspotOverrides[qid] = null;
        updateHotspot();
      });

      hotspotEditBar.querySelector('[data-bar-copy]').addEventListener('click', function () {
        const out = {};
        Object.keys(registry.questions || {}).forEach(function (qid) {
          const hs = getCurrentHotspot(qid);
          if (hs) out[qid] = { x: Math.round(hs.x), y: Math.round(hs.y) };
        });
        const json = JSON.stringify(out, null, 2);
        const done = function () {
          const span = hotspotEditBar.querySelector('[data-bar-text]');
          if (span) span.textContent = 'copied — paste back to me';
          setTimeout(updateHotspotEditBar, 1800);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(json).then(done, done);
        } else {
          const ta = document.createElement('textarea');
          ta.value = json;
          document.body.appendChild(ta);
          ta.select();
          try { document.execCommand('copy'); } catch (_) {}
          document.body.removeChild(ta);
          done();
        }
      });

      // Drag handlers with click-offset tracking. Offset captured on
      // pointerdown so a click without movement doesn't snap the bloom
      // to the click position (that's how dots were ending up at (0,0)
      // and (480,480) in round 1).
      if (!els.hotspot) return;
      let dragging = false;
      const dragOffset = { x: 0, y: 0 };

      function cursorToViewBox(e) {
        const rect = els.imageWrap && els.imageWrap.getBoundingClientRect();
        if (!rect || !rect.width || !rect.height) return null;
        return {
          x: ((e.clientX - rect.left) / rect.width)  * 480,
          y: ((e.clientY - rect.top)  / rect.height) * 480
        };
      }

      els.hotspot.addEventListener('pointerdown', function (e) {
        dragging = true;
        e.preventDefault();
        try { e.target.setPointerCapture(e.pointerId); } catch (_) {}
        const c = cursorToViewBox(e);
        const qid = activeQuestionId();
        const existing = qid ? getCurrentHotspot(qid) : null;
        if (c && existing) {
          dragOffset.x = c.x - existing.x;
          dragOffset.y = c.y - existing.y;
        } else {
          dragOffset.x = 0;
          dragOffset.y = 0;
        }
      });
      els.hotspot.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        const c = cursorToViewBox(e);
        if (!c) return;
        const qid = activeQuestionId();
        if (!qid) return;
        hotspotOverrides[qid] = {
          x: Math.max(0, Math.min(480, c.x - dragOffset.x)),
          y: Math.max(0, Math.min(480, c.y - dragOffset.y))
        };
        updateHotspot();
      });
      els.hotspot.addEventListener('pointerup',     function () { dragging = false; });
      els.hotspot.addEventListener('pointercancel', function () { dragging = false; });
    }

    // Pick the band-anchored silhouette + the skin-tone-answer-driven colour
    // and apply them to the .sizing-quiz__skin-tone layer behind the bra
    // figure. Mirrors the live quiz's two-layer composition.
    function updateSkinTone() {
      if (!els.skinTone) return;

      // Apply mask + colour together; clear them together. Keeping the
      // background-color set while the mask is empty makes the whole
      // image-wrap render as a solid coloured square (the bug we just hit
      // on country_size where no band has been picked yet).
      const skinSlug = slugForValue(state.answers.skin_tone);
      const color = SKIN_TONE_COLORS[skinSlug] || DEFAULT_SKIN_COLOR;
      function applyMask(url) {
        els.skinTone.style.setProperty('--sq-skin-color', color);
        els.skinTone.style.setProperty('--sq-skin-mask', 'url("' + url + '")');
      }
      function clearMask() {
        els.skinTone.style.removeProperty('--sq-skin-color');
        els.skinTone.style.removeProperty('--sq-skin-mask');
      }

      const q = activeQuestion();
      // 1. Per-option override
      const optionSkin = selectedOptionAsset(q, 'skin_tone');
      if (optionSkin && cfg.skinToneVariants[optionSkin]) {
        applyMask(cfg.skinToneVariants[optionSkin]);
        return;
      }
      // 2. Question-level view override
      if (q && q.view === 'back') {
        if (cfg.skinToneVariants.back) applyMask(cfg.skinToneVariants.back);
        else clearMask();
        return;
      }
      // 3. TOT-anchored front-view lookup. When band hasn't been picked
      // yet (country_size and earlier in the path), default to the tot3
      // anchor so the silhouette still shows — matches the default
      // figure src (quiz-figure-tot3-tot0.svg) on the bra-image layer.
      const tableSlug = countryTableSlug(slugForValue(state.answers.country));
      const country = sizes && sizes.countries && tableSlug ? sizes.countries[tableSlug] : null;
      const totBand = country ? lookupTotBand(country, state.answers.band) : null;
      const effectiveTotBand = totBand != null ? totBand : 3;
      const anchor = nearestLowerAnchor(effectiveTotBand, SKIN_TONE_BAND_ANCHORS);
      const url = cfg.skinTones[anchor];
      if (!url) { clearMask(); return; }
      applyMask(url);
    }

    // -------------------------------------------------------- network

    function startPayload() {
      return {
        session_id: state.session_id,
        name: state.answers.name || null,
        path: state.path,
        started_at: state.started_at,
        user_agent: navigator.userAgent,
        referrer: document.referrer || null
      };
    }

    function autosavePayload() {
      return {
        answers: state.answers,
        last_question_id: state.last_question_id,
        current_path: state.path
      };
    }

    function completePayload(email) {
      const payload = {
        answers: state.answers,
        email: email || null,
        completed_at: new Date().toISOString()
      };
      if (email && gateToken) payload.turnstile_token = gateToken;
      return payload;
    }

    function kickStart() {
      // Fire only after the first non-name answer commits.
      if (state._start_done || state.quiz_id) return;
      const lq = state.last_question_id;
      if (!lq || lq === 'name') return;

      state._start_in_flight = true;
      mockApi.start(startPayload()).then(function (resp) {
        state.quiz_id = resp.quiz_id;
        state._start_done = true;
        state._start_in_flight = false;
        persist(state);
        // Drain any queued autosaves
        const queued = state._start_pending_queue.slice();
        state._start_pending_queue = [];
        queued.forEach(function () { kickAutosave(); });
        // Always do one fresh autosave so the server has the latest state
        kickAutosave();
      }).catch(function (err) {
        state._start_in_flight = false;
        console.warn('[quiz] /start failed; will retry next forward', err);
      });
    }

    function kickAutosave() {
      const payload = autosavePayload();
      if (!state.quiz_id) {
        // Queue and bail; will flush when /start returns
        state._start_pending_queue.push(payload);
        return;
      }
      // Flush any prior pending payload first
      if (state.pending_autosave) {
        const prior = state.pending_autosave;
        state.pending_autosave = null;
        sendAutosaveWithRetry(prior);
      }
      sendAutosaveWithRetry(payload);
    }

    function sendAutosaveWithRetry(payload) {
      function attempt(n) {
        return mockApi.autosave(state.quiz_id, payload).then(function () {
          state._autosave_failure_started_at = null;
        }).catch(function (err) {
          if (n >= 3) {
            // Queue locally for next forward
            state.pending_autosave = payload;
            persist(state);
            if (!state._autosave_failure_started_at) {
              state._autosave_failure_started_at = Date.now();
            } else if (Date.now() - state._autosave_failure_started_at > AUTOSAVE_FAILURE_WARN_MS) {
              console.warn('[quiz] autosave has been failing for >5 minutes', err);
            }
            return;
          }
          const delay = Math.pow(2, n - 1) * 1000; // 1s, 2s, 4s
          setTimeout(function () { attempt(n + 1); }, delay);
        });
      }
      attempt(1);
    }

    // --------------------------------------------------- email capture gate

    // Set once the user submits a valid email on the capture screen; gates
    // whether the report shows a discount banner. Skippers leave it false.
    let discountEarned = false;

    // The screen shown after the last question, before the report. Collects
    // an email (→ discount code on the report) with a Skip escape hatch.
    // The Turnstile token from the last email submit, sent with /complete.
    let gateToken = '';

    // errorMessage: shown when the API sent the shopper back (failed bot
    // check, too many tries).
    function showEmailCapture(errorMessage) {
      // Defensive: if the section markup predates this feature (cached
      // template), skip the gate rather than throwing on a null container.
      if (!els.emailCapture) { kickComplete(); return; }
      const heading = 'Your Results Are In…';
      const body = "We'd love to send you a copy and give you 20% off your first order.";
      els.emailCapture.innerHTML =
        '<div class="sizing-quiz__email">' +
          '<h3 class="sizing-quiz__email-heading">' + escapeHtml(heading) + '</h3>' +
          '<p class="sizing-quiz__email-body">' + escapeHtml(body) + '</p>' +
          '<form class="sizing-quiz__email-form" data-sq-email-form novalidate>' +
            '<div class="sizing-quiz__email-row">' +
              '<input class="sizing-quiz__email-input" type="email" name="email" ' +
                'autocomplete="email" inputmode="email" placeholder="you@example.com" required>' +
            '</div>' +
            '<p class="sizing-quiz__email-error" data-sq-email-error hidden>Please enter a valid email address.</p>' +
            '<div class="sizing-quiz__turnstile" data-sq-turnstile></div>' +
            '<div class="sizing-quiz__email-actions">' +
              brandBtn('button', 'next', 'get discount', 'type="submit" data-sq-email-submit') +
              '<button type="button" class="sizing-quiz__email-skip" data-sq-email-skip>Skip</button>' +
            '</div>' +
          '</form>' +
        '</div>';

      setStageView('email');
      scrollSectionToView();

      const form = els.emailCapture.querySelector('[data-sq-email-form]');
      const input = els.emailCapture.querySelector('input[name="email"]');
      const errEl = els.emailCapture.querySelector('[data-sq-email-error]');
      const skip = els.emailCapture.querySelector('[data-sq-email-skip]');

      // A fresh widget on every render — also how a failed check gets reset.
      const turnstile = mountTurnstile(form.querySelector('[data-sq-turnstile]'), cfg.turnstileSiteKey);
      function showErr(msg) {
        if (!errEl) return;
        errEl.textContent = msg;
        errEl.hidden = false;
      }
      if (errorMessage) showErr(errorMessage);

      // Prefill if we already captured an email earlier this session.
      if (state.answers.email) input.value = state.answers.email;
      if (input) input.addEventListener('input', function () { if (errEl) errEl.hidden = true; });

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        const email = (input.value || '').trim();
        if (!EMAIL_RE.test(email)) {
          showErr('Please enter a valid email address.');
          input.focus();
          return;
        }
        gateToken = '';
        if (turnstile) {
          gateToken = turnstile.token();
          if (!gateToken && !turnstile.unavailable()) {
            showErr('Just a moment while we check you\u2019re not a robot, then try again.');
            return;
          }
        }
        if (errEl) errEl.hidden = true;
        state.answers.email = email;
        discountEarned = true;
        persist(state);
        kickComplete();
      });

      if (skip) {
        skip.addEventListener('click', function () {
          discountEarned = false;
          kickComplete();
        });
      }
    }

    function kickComplete() {
      setStageView('loading');
      const startedAt = Date.now();
      const rotateHandle = startRotatingMessages();

      // Need a quiz_id; if /start hasn't returned yet, force-start synchronously.
      const ensureId = state.quiz_id
        ? Promise.resolve(state.quiz_id)
        : mockApi.start(startPayload()).then(function (resp) {
            state.quiz_id = resp.quiz_id;
            state._start_done = true;
            return resp.quiz_id;
          });

      ensureId
        .then(function (id) { return mockApi.complete(id, completePayload(state.answers.email)); })
        .then(function (recommendation) {
          const elapsed = Date.now() - startedAt;
          const wait = Math.max(0, MIN_LOADING_MS - elapsed);
          setTimeout(function () {
            stopRotatingMessages(rotateHandle);
            renderResults(recommendation);
            clearPersisted();
          }, wait);
        })
        .catch(function (err) {
          console.error('[quiz] /complete failed', err);
          stopRotatingMessages(rotateHandle);
          // The bot check failed or too many tries: back to the email form.
          if (err && (err.detail === 'verification_failed' || err.status === 429)) {
            showEmailCapture(err.status === 429
              ? 'Too many tries from here. Please wait a few minutes and try again.'
              : 'We couldn\u2019t check you\u2019re not a robot. Please try again.');
            return;
          }
          showError();
        });
    }

    function startRotatingMessages() {
      const messages = DEFAULT_LOADING_MESSAGES;
      let i = 0;
      els.loadingText.textContent = messages[0];
      const handle = setInterval(function () {
        els.loadingText.classList.add('is-fading');
        setTimeout(function () {
          i = (i + 1) % messages.length;
          els.loadingText.textContent = messages[i];
          els.loadingText.classList.remove('is-fading');
        }, 220);
      }, ROTATE_MS + 220);
      return handle;
    }

    function stopRotatingMessages(handle) { clearInterval(handle); }

    function showError() {
      setStageView('error');
    }

    function retryFromError() {
      setStageView('loading');
      kickComplete();
    }

    // -------------------------------------------------------- results

    function renderResults(rec) {
      const symptoms = symptomsToList(rec.symptoms_flags || {}, rec.symptoms_labels);
      // TOT band/cup land in the prominent tiles up top; the country-localised
      // display is the smaller "closest industry sizing" subtitle below.
      const totBand = rec.recommended_tot_band != null ? String(rec.recommended_tot_band) : '';
      const totCup  = rec.recommended_tot_cup  != null ? String(rec.recommended_tot_cup)  : '';
      const displayBand = rec.recommended_display_band || '';
      const displayCup  = rec.recommended_display_cup  || '';

      // Discount banner — only for users who handed over an email at the
      // capture gate. Rendered hidden and revealed once the code resolves
      // (or left hidden if the lookup failed — non-fatal, never blocks the
      // report).
      const discountHtml = discountEarned
        ? '<div class="sizing-quiz__discount" data-sq-discount hidden>' +
            '<p class="sizing-quiz__discount-label">Your 20% off code</p>' +
            '<p class="sizing-quiz__discount-code" data-sq-discount-code></p>' +
            '<p class="sizing-quiz__discount-note">Use this at checkout for 20% off your first order</p>' +
          '</div>'
        : '';

      const html =
        '<div class="sizing-quiz__results-header">' +
          '<p class="sizing-quiz__results-name">Results</p>' +
          '<p class="sizing-quiz__results-size-label">Recommended size</p>' +
          '<div class="sizing-quiz__size-tiles">' +
            '<div class="sizing-quiz__size-tile">' +
              '<span class="sizing-quiz__size-tile-label">Band</span>' +
              '<span class="sizing-quiz__size-tile-value">' + escapeHtml(totBand) + '</span>' +
            '</div>' +
            '<div class="sizing-quiz__size-tile">' +
              '<span class="sizing-quiz__size-tile-label">Cup</span>' +
              '<span class="sizing-quiz__size-tile-value">' + escapeHtml(totCup) + '</span>' +
            '</div>' +
          '</div>' +
          '<p class="sizing-quiz__industry-size">Closest industry sizing is: ' +
            escapeHtml(displayBand + displayCup) +
          '</p>' +
        '</div>' +

        discountHtml +

        (symptoms.length
          ? '<h3 class="sizing-quiz__section-heading">Your symptoms</h3>' +
            '<ul class="sizing-quiz__symptoms">' +
              symptoms.map(function (s) { return '<li>' + escapeHtml(s) + '</li>'; }).join('') +
            '</ul>'
          : '') +

        '<h3 class="sizing-quiz__section-heading">Your report</h3>' +
        '<p class="sizing-quiz__report">' + escapeHtml(rec.placeholder_report || '') + '</p>' +

        '<h3 class="sizing-quiz__section-heading">Shop your size from our collection</h3>' +
        '<div class="sizing-quiz__products" data-sq-products>' +
          '<p class="sizing-quiz__products-loading">Loading products…</p>' +
        '</div>' +

        '<div class="sizing-quiz__results-actions">' +
          brandBtn('button', 'prev', 'start over', 'type="button" data-sq-restart', REFRESH_SVG) +
          // Primary bottom CTA, populated by renderShoppableProducts() once
          // matches are known. Hidden by default; revealed + wired when a
          // matching variant is found.
          '<button class="button" type="button" data-sq-results-cta hidden>Add my recommended size to cart →</button>' +
        '</div>';

      els.results.innerHTML = html;
      setStageView('results');
      scrollSectionToView();

      const restart = els.results.querySelector('[data-sq-restart]');
      if (restart) restart.addEventListener('click', restartQuiz);

      // Fill + reveal the discount banner once the code resolves. Left
      // hidden if the user skipped the gate or the backend lookup failed.
      if (discountEarned) {
        const banner = els.results.querySelector('[data-sq-discount]');
        const codeEl = els.results.querySelector('[data-sq-discount-code]');
        // The shopper's unique code comes back from /complete (none if the
        // pool was empty or they've used one before). Offline preview: a stub.
        const issued = rec && rec.discount && rec.discount.code
          ? rec.discount
          : (useReal ? null : { code: 'WELCOME20', expires_at: null });
        if (banner && codeEl && issued) {
          codeEl.textContent = String(issued.code);
          if (issued.expires_at) {
            const note = banner.querySelector('.sizing-quiz__discount-note');
            const date = new Date(issued.expires_at).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
            if (note) note.textContent += '. Expires ' + date + '.';
          }
          banner.hidden = false;
        }
      }

      // Shoppable results: read the Liquid-embedded bra catalog, find the
      // single variant per product that matches (recommended_tot_band,
      // recommended_tot_cup) and is in stock, render those product cards.
      // Mirror of the Bubble app's filter chain:
      //   Collection-scoped products (set in section settings, default 'all')
      //   → product.type == 'Bra' and not tagged 'archive'      (Liquid layer)
      //   → variant.available && option1 == band && option2 == cup (here)
      renderShoppableProducts(rec);
    }

    // Read the Liquid-embedded <script data-sq-bra-catalog> and parse to JS.
    // Returns [] on missing/invalid blob so caller can render an empty state.
    function readBraCatalog() {
      const el = root.querySelector('[data-sq-bra-catalog]');
      if (!el) return [];
      const raw = (el.textContent || '').trim();
      if (!raw) return [];
      try { return JSON.parse(raw); }
      catch (e) {
        console.warn('[quiz] bra catalog JSON parse failed', e);
        return [];
      }
    }

    // The catalog mixes "1" / "1." for band and "6" / ".6" for cup across
    // products. Strip periods so equality comparisons work either way. Both
    // sides of the comparison go through this so the test answer ("12") and
    // catalog value ("12.") collapse to the same string.
    function normalizeSizeValue(v) {
      return String(v == null ? '' : v).replace(/\./g, '').trim();
    }

    // Returns [{ product, variant }] — one entry per product that has an
    // in-stock variant matching the recommended (tot_band, tot_cup).
    function findMatchingVariants(catalog, totBand, totCup) {
      if (totBand == null || totCup == null) return [];
      const bandKey = String(totBand);
      const cupKey  = String(totCup);
      const matches = [];
      for (let i = 0; i < catalog.length; i++) {
        const product = catalog[i];
        if (!product || !Array.isArray(product.variants)) continue;
        const variant = product.variants.find(function (v) {
          return v
            && v.available === true
            && normalizeSizeValue(v.option1) === bandKey
            && normalizeSizeValue(v.option2) === cupKey;
        });
        if (variant) matches.push({ product: product, variant: variant });
      }
      return matches;
    }

    function renderShoppableProducts(rec) {
      const productsEl = els.results.querySelector('[data-sq-products]');
      if (!productsEl) return;

      const catalog = readBraCatalog();
      if (!catalog.length) {
        productsEl.innerHTML = '<p class="sizing-quiz__products-empty">' +
          'Product catalog isn\'t configured for this page yet. ' +
          'Set the "Bra catalog collection handle" in the section settings.' +
        '</p>';
        return;
      }

      const matches = findMatchingVariants(
        catalog,
        rec.recommended_tot_band,
        rec.recommended_tot_cup
      );

      if (!matches.length) {
        productsEl.innerHTML = '<p class="sizing-quiz__products-empty">' +
          'We don\'t currently stock your recommended fit ' +
          '(Band ' + escapeHtml(String(rec.recommended_tot_band)) +
          ', Cup ' + escapeHtml(String(rec.recommended_tot_cup)) + '). ' +
          'Reach out and we\'ll let you know when it\'s back.' +
        '</p>';
        return;
      }

      // Flat grid of every matching product. Earlier we grouped by primary
      // collection to mirror Bubble; result looked weird because most bras
      // belong to multiple collections and the "first" collection per
      // product.collections is unpredictable (smart collections, manual
      // collections, line collections all mixed). Showing everything at
      // once is cleaner and matches the user direction.
      productsEl.innerHTML = '<div class="sizing-quiz__products-grid">' +
        matches.map(function (m) { return shoppableCardHtml(m, rec); }).join('') +
      '</div>';
      attachAddToCartHandlers(productsEl);

      // Wire the bottom-of-results CTA to the first matched product.
      // "Add my recommended size to cart" — uses the same /cart/add.js path
      // as the product cards.
      const cta = els.results.querySelector('[data-sq-results-cta]');
      if (cta && matches.length) {
        const first = matches[0];
        const productName = (first.product && first.product.title) || '';
        cta.setAttribute('data-variant-id', String(first.variant.id));
        cta.textContent = 'Add ' + productName + ' to cart →';
        cta.hidden = false;
        attachAddToCartHandlers(cta.parentNode);
      }
    }

    function shoppableCardHtml(match, rec) {
      const product = match.product;
      const variant = match.variant;
      const img = product.image || '';
      const url = product.url || ('/products/' + (product.handle || ''));
      const title = product.title || '';
      const priceStr = formatVariantPrice(variant.price);
      // Cards lead with the TOT size (Band/Cup) to match the recommendation
      // tiles up top — not the "closest industry sizing" (e.g. 8DD), which is
      // only a subtitle. Fall back to the matched variant's options.
      const totBand = rec && rec.recommended_tot_band != null
        ? String(rec.recommended_tot_band)
        : normalizeSizeValue(variant.option1);
      const totCup = rec && rec.recommended_tot_cup != null
        ? String(rec.recommended_tot_cup)
        : normalizeSizeValue(variant.option2);
      const sizeLabel = totBand + '/' + totCup;

      return '<div class="sizing-quiz__product">' +
        (img
          ? '<a class="sizing-quiz__product-image-link" href="' + escapeAttr(url) + '">' +
              '<img class="sizing-quiz__product-image" src="' + escapeAttr(img) + '" alt="' + escapeAttr(title) + '" loading="lazy">' +
            '</a>'
          : '<div class="sizing-quiz__product-image sizing-quiz__product-image--placeholder"></div>') +
        '<a class="sizing-quiz__product-title" href="' + escapeAttr(url) + '">' + escapeHtml(title) + '</a>' +
        '<p class="sizing-quiz__product-size">' + escapeHtml(sizeLabel) + '</p>' +
        (priceStr ? '<p class="sizing-quiz__product-price">' + escapeHtml(priceStr) + '</p>' : '') +
        '<button class="button sizing-quiz__product-cart" type="button" data-variant-id="' + escapeAttr(String(variant.id)) + '">Add to cart</button>' +
      '</div>';
    }

    // Liquid emits variant.price as the integer cents value (e.g. 5500 = $55.00).
    // We default to AUD ($) — One Two's primary currency.
    function formatVariantPrice(cents) {
      if (cents == null || cents === '') return '';
      const n = Number(cents);
      if (!isFinite(n)) return '';
      return '$' + (n / 100).toFixed(2);
    }

    function attachAddToCartHandlers(scope) {
      scope.querySelectorAll('[data-variant-id]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          const variantId = btn.dataset.variantId;
          if (!variantId) return;
          const original = btn.textContent;
          btn.disabled = true;
          btn.textContent = 'Adding…';
          fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
          })
            .then(function (r) {
              if (!r.ok) throw new Error('add status ' + r.status);
              return r.json();
            })
            .then(function () {
              btn.textContent = 'Added ✓';
              setTimeout(function () { btn.textContent = original; btn.disabled = false; }, 1600);
            })
            .catch(function (err) {
              console.warn('[quiz] add to cart failed', err);
              btn.textContent = 'Try again';
              btn.disabled = false;
            });
        });
      });
    }

    function restartQuiz() {
      clearPersisted();
      const fresh = freshState();
      // Clear the existing object's keys so the boot-time `state` reference
      // we already closed over keeps working.
      Object.keys(state).forEach(function (k) { delete state[k]; });
      Object.assign(state, fresh);
      applyForcedAnswers();
      if (els.image) els.image.setAttribute('src', cfg.images.default);
      els.results.innerHTML = '';
      setStageView('questions');
      renderQuestion();
      scrollSectionToView();
    }

    function symptomsToList(flags, labels) {
      // Prefer server-supplied labels; fall back to humanising the flag key.
      labels = labels || {};
      return Object.keys(flags).filter(function (k) { return flags[k]; }).map(function (k) {
        return labels[k] || k.replace(/_/g, ' ');
      });
    }

    // ---------------------------------------------------- stage listeners

    function attachStageListeners(q, id) {
      // re-evaluate validity on any input/change inside the stage
      els.stage.addEventListener('input', stageChanged);
      els.stage.addEventListener('change', stageChanged);
      if (q.type === 'country_size') attachCountrySizeListeners(id);
    }

    function attachCountrySizeListeners(id) {
      const countryRadios = els.stage.querySelectorAll('input[name="' + cssEscape(id) + '__country"]');
      const bandSelect = els.stage.querySelector('select[name="' + cssEscape(id) + '__band"]');
      const cupSelect  = els.stage.querySelector('select[name="' + cssEscape(id) + '__cup"]');
      const step2 = els.stage.querySelector('[data-sq-step-2]');
      const cupField = els.stage.querySelector('[data-sq-cup-field]');

      function getCheckedCountry() {
        const checked = els.stage.querySelector('input[name="' + cssEscape(id) + '__country"]:checked');
        return checked ? checked.value : null;
      }

      function buildOptions(values) {
        return '<option value="">Select…</option>' + values.map(function (v) {
          return '<option value="' + escapeAttr(v) + '">' + escapeHtml(v) + '</option>';
        }).join('');
      }

      // Band + cup options come straight from the /sizes matrix (the DB), so
      // the dropdowns always match what the backend can actually recommend —
      // no hardcoded per-country lists to drift out of sync. `countrySlug` is
      // the registry slug (e.g. "France"); countryTableSlug maps it to the
      // /sizes table key (e.g. "france", "us").
      function sizesForCountry(countrySlug) {
        if (!sizes || !sizes.countries) return null;
        const tableSlug = countryTableSlug(countrySlug);
        return (tableSlug && sizes.countries[tableSlug]) || null;
      }

      function populateBand(countrySlug) {
        const country = sizesForCountry(countrySlug);
        let values;
        if (country && Array.isArray(country.bands)) {
          values = country.bands.slice()
            .sort(function (a, b) { return a.tot_band - b.tot_band; })
            .map(function (b) { return String(b.country_band); });
        } else {
          // /sizes unavailable (offline dev / API down): fall back to the
          // static registry so the step still renders. Production has /sizes.
          values = (registry.country_band_options || {})[countrySlug] || [];
        }
        bandSelect.innerHTML = buildOptions(values);
      }

      // Cups are band-aware: only the cups that actually exist for the chosen
      // band (sister sizing) are offered, so an impossible (band, cup) pair
      // can't be selected. Ordered by tot_cup ascending.
      function populateCup(countrySlug, band) {
        const country = sizesForCountry(countrySlug);
        let values;
        if (country && Array.isArray(country.sizes) && band != null && band !== '') {
          const b = String(band);
          const seen = {};
          values = country.sizes
            .filter(function (r) { return String(r.country_band) === b; })
            // Order by tot_cup; at the floor several small cups collapse onto
            // the same tot_cup, so break ties by label length desc ("AA"
            // before "A") to keep the conventional smallest-first order.
            .sort(function (a, c) {
              return (a.tot_cup - c.tot_cup) ||
                     (String(c.country_cup).length - String(a.country_cup).length);
            })
            .map(function (r) { return String(r.country_cup); })
            .filter(function (c) { if (seen[c]) return false; seen[c] = true; return true; });
        } else {
          values = (registry.country_cup_options || {})[countrySlug] || [];
        }
        cupSelect.innerHTML = buildOptions(values);
      }

      function onCountryChange() {
        const cUuid = getCheckedCountry();
        if (!cUuid) return;
        const cSlug = slugForValue(cUuid);
        if (!cSlug) return;
        step2.hidden = false;
        populateBand(cSlug);
        bandSelect.value = '';
        cupField.hidden = true;
        cupSelect.value = '';
      }

      function onBandChange() {
        if (!bandSelect.value) return;
        const cUuid = getCheckedCountry();
        if (!cUuid) return;
        const cSlug = slugForValue(cUuid);
        if (!cSlug) return;
        cupField.hidden = false;
        populateCup(cSlug, bandSelect.value);
        cupSelect.value = '';
        // Mirror band into top-level state.answers so updateImage() picks
        // the correct band-anchored skin-tone silhouette as soon as band
        // is chosen — don't wait for cup or Next.
        state.answers.band = bandSelect.value;
        updateImage();
      }

      function onCupChange() {
        // Mirror to top-level state.answers so updateImage() sees the new cup
        // immediately, before the user has clicked Next.
        state.answers.cup = cupSelect.value || null;
        updateImage();
      }

      countryRadios.forEach(function (r) { r.addEventListener('change', onCountryChange); });
      bandSelect.addEventListener('change', onBandChange);
      cupSelect.addEventListener('change', onCupChange);

      // Restore from persisted state.answers
      const countryUuid = state.answers.country;
      const band = state.answers.band;
      const cup = state.answers.cup;
      if (countryUuid) {
        const radio = els.stage.querySelector('input[name="' + cssEscape(id) + '__country"][value="' + cssEscape(countryUuid) + '"]');
        if (radio) radio.checked = true;
        const countrySlug = slugForValue(countryUuid);
        if (countrySlug) {
          step2.hidden = false;
          populateBand(countrySlug);
          if (band) {
            bandSelect.value = band;
            cupField.hidden = false;
            populateCup(countrySlug, band);
            if (cup) cupSelect.value = cup;
          }
        }
      }
    }

    function stageChanged() {
      updateNav();
      updateMeasureEchoes();
      const id = activeQuestionId();
      const q = activeQuestion();

      // Slider: update the "currently selected" label as the user drags.
      if (q && q.type === 'single_select' && q.render_as === 'slider') {
        updateSliderValueLabel(id);
        const v = readAnswerFromDOM(q, id);
        if (v != null) state.answers[id] = v;
      }

      // Walk every question that's currently visible on the page —
      // the active parent plus any (potentially nested) inline
      // children. For each one that has children_when, re-render
      // ITS children section if its answer changed since last paint.
      // Re-rendering only on real answer changes prevents an in-flight
      // click on a nested child from wiping itself out (stageChanged
      // fires on every input event, including those inside child
      // sections).
      function walkVisible(parentQ, parentId, acc) {
        if (!parentQ) return;
        acc.push({ q: parentQ, id: parentId });
        const cids = collectChildIds(parentQ, parentId);
        cids.forEach(function (cid) {
          const childQ = registry.questions && registry.questions[cid];
          if (childQ) walkVisible(childQ, cid, acc);
        });
      }
      const visible = [];
      walkVisible(q, id, visible);

      visible.forEach(function (item) {
        if (!item.q.children_when) return;
        const v = readAnswerFromDOM(item.q, item.id);
        if (v !== undefined && v !== state.answers[item.id]) {
          state.answers[item.id] = v;
          const container = els.stage.querySelector(
            '[data-sq-children-for="' + cssEscape(item.id) + '"]'
          );
          if (container) {
            const cids = collectChildIds(item.q, item.id);
            container.innerHTML = cids.map(function (cid) {
              const childQ = registry.questions && registry.questions[cid];
              if (!childQ) return '';
              return '<fieldset class="sizing-quiz__child" data-sq-child-id="' + escapeAttr(cid) + '">' +
                     htmlForQuestion(childQ, cid) + '</fieldset>';
            }).join('');
            cids.forEach(function (cid) {
              const childQ = registry.questions[cid];
              const prev = state.answers[cid];
              if (childQ && prev != null) writeAnswerToDOM(childQ, cid, prev);
            });
          }
        }
      });

      // Mirror every visible question's current DOM answer into state
      // so commitCurrent + validation see it without a Next round-trip.
      visible.forEach(function (item) {
        const v = readAnswerFromDOM(item.q, item.id);
        if (v !== undefined) state.answers[item.id] = v;
      });

      // Live-update the figure on inputs that affect it (don't wait for Next):
      //   - cup: changes the bra-image swap
      //   - skin_tone: changes the body silhouette colour
      //   - any question with per-option figure/skin_tone overrides
      //     (e.g. band_position_eod): the selected option drives the visual
      const hasOptionOverride = q && Array.isArray(q.options) && q.options.some(function (o) {
        return o && (o.figure || o.skin_tone);
      });
      if (id === 'cup' || id === 'skin_tone' || hasOptionOverride) {
        const v = readAnswerFromDOM(q, id);
        if (v != null) {
          state.answers[id] = v;
          updateImage();
        }
      }
      // Number inputs render no inline tip/error text. Invalid input
      // keeps Next disabled via isCurrentAnswerValid; out-of-range values
      // pass through and are dropped server-side by the API failsafe.
    }

    // ----------------------------------------------------- root listeners

    els.back.addEventListener('click', back);
    els.next.addEventListener('click', next);
    els.submit.addEventListener('click', submit);
    if (els.errorRetry) els.errorRetry.addEventListener('click', retryFromError);
    if (els.restartGlobal) els.restartGlobal.addEventListener('click', restartQuiz);

    // Initial render
    setStageView('questions');
    if (state.answers.cup) updateImage();
    renderQuestion();

    initHotspotEditMode();

    // Expose a tiny debug surface for manual testing
    if (window.__quizDebug == null) window.__quizDebug = {};
    window.__quizDebug.state = state;
    window.__quizDebug.registry = registry;
    window.__quizDebug.reset = function () { clearPersisted(); window.location.reload(); };
  }

  // -------------------------------------------------------- restored sanity

  function validateRestored(state, registry) {
    if (!Array.isArray(state.question_ids) || state.question_ids.length === 0) return false;
    if (state.current_index < 0 || state.current_index >= state.question_ids.length) return false;
    return state.question_ids.every(function (qid) {
      return qid === 'name' || qid === 'confidence' || qid === 'wired_bra' ||
             qid === 'wireless_with_cup' || !!registry.questions[qid];
    });
  }

  // ------------------------------------------------------------ utilities

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function escapeAttr(s) { return escapeHtml(s); }
  function cssEscape(s) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(s);
    return String(s).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  // --------------------------------------------------------- bootstrap

  function bootAll() {
    document.querySelectorAll('[data-sizing-quiz]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  // Re-init on Shopify customiser section reload
  document.addEventListener('shopify:section:load', function (e) {
    const root = (e.target || document).querySelector ? (e.target.querySelector('[data-sizing-quiz]') || (e.target.matches && e.target.matches('[data-sizing-quiz]') ? e.target : null)) : null;
    if (root) {
      delete root.dataset.sizingQuizInitialized;
      init(root);
    }
  });
})();
